import {
  Archive,
  Bookmark,
  CalendarCheck2,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  CircleDashed,
  Clock3,
  createIcons,
  FolderKanban,
  KeyRound,
  Link2,
  ListTodo,
  LoaderCircle,
  LogIn,
  LogOut,
  Mail,
  MoreHorizontal,
  Plus,
  Repeat2,
  SkipForward,
  Sun,
  Trash2,
  X,
} from "lucide";
import { createRepository } from "./data/repository.js";
import { authRedirectUrl, isSupabaseConfigured, supabase } from "./data/supabase.js";
import { parseSharedText } from "./link-parser.js";
import {
  dateKeyToDate,
  deleteProjectFromWorkspace,
  emptyWorkspace,
  eventDuration,
  isTaskOverdue,
  localDateKey,
  nextOccurrenceDate,
  normalizeRecurrenceRule,
  snappedTimePlacement,
  taskEffectiveDate,
  taskOverdueDays,
} from "./domain.js";
import "../styles.css";

const HOUR_HEIGHT = 72;
const SNAP = 15;
const DAY_MINUTES = 24 * 60;
const DEFAULT_COLOR = "terracotta";
const COLORS = {
  terracotta: { label: "陶土", solid: "#c76848", soft: "#f4ded1", ink: "#704331" },
  sage: { label: "鼠尾草", solid: "#5c7567", soft: "#dce7df", ink: "#3f5549" },
  blue: { label: "雾蓝", solid: "#56728f", soft: "#dbe6f0", ink: "#3c536a" },
  gold: { label: "琥珀", solid: "#b2833e", soft: "#f0dfb9", ink: "#705629" },
};
const DAY_NAMES = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
const WEEKDAY_SHORT = ["日", "一", "二", "三", "四", "五", "六"];
const ICONS = {
  Archive,
  Bookmark,
  CalendarCheck2,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  CircleDashed,
  Clock3,
  FolderKanban,
  KeyRound,
  Link2,
  ListTodo,
  LoaderCircle,
  LogIn,
  LogOut,
  Mail,
  MoreHorizontal,
  Plus,
  Repeat2,
  SkipForward,
  Sun,
  Trash2,
  X,
};

const state = {
  workspace: emptyWorkspace(),
  filter: "unplanned",
  view: "day",
  cursor: startOfDay(new Date()),
  dragging: null,
  dropPreview: null,
  dragGhost: null,
  resizing: null,
  colorMenuOpen: false,
  selectedColor: DEFAULT_COLOR,
  editWeekdays: [],
  mobileView: "tasks",
  authMethod: "password",
  todayKey: localDateKey(new Date()),
};

let repository = null;
let currentUser = null;
let sessionRevision = 0;
let saveQueue = Promise.resolve();
let midnightRefreshTimer = null;

document.querySelector("#app").innerHTML = `
  <section class="auth-gate" id="authGate" aria-labelledby="authTitle" hidden>
    <div class="auth-panel">
      <header class="auth-brand">
        <div class="brand-mark" aria-hidden="true"><i data-lucide="calendar-check-2"></i></div>
        <div><strong>节奏</strong><span>把想做的事，放进真实的时间里</span></div>
      </header>
      <div class="auth-heading">
        <p class="eyebrow">跨设备同步</p>
        <h1 id="authTitle">回到你的日程</h1>
        <p>登录后，Mac 和 iPhone 会使用同一份任务与日程数据。</p>
      </div>
      <div class="auth-methods" role="tablist" aria-label="登录方式">
        <button class="active" type="button" role="tab" aria-selected="true" data-auth-method="password"><i data-lucide="key-round"></i><span>密码</span></button>
        <button type="button" role="tab" aria-selected="false" data-auth-method="magic"><i data-lucide="mail"></i><span>Magic Link</span></button>
      </div>
      <form class="auth-form" id="passwordAuthForm">
        <label><span>Email</span><input id="passwordEmail" type="email" autocomplete="email" placeholder="you@example.com" required /></label>
        <label><span>密码</span><input id="authPassword" type="password" autocomplete="current-password" minlength="6" placeholder="至少 6 位" required /></label>
        <button class="auth-primary" type="submit" data-idle-label="登录"><i data-lucide="log-in"></i><span>登录</span></button>
        <button class="auth-secondary" id="signUpButton" type="button" data-idle-label="创建账号"><span>创建账号</span></button>
      </form>
      <form class="auth-form" id="magicAuthForm" hidden>
        <label><span>Email</span><input id="magicEmail" type="email" autocomplete="email" placeholder="you@example.com" required /></label>
        <button class="auth-primary" type="submit" data-idle-label="发送登录链接"><i data-lucide="mail"></i><span>发送登录链接</span></button>
        <p class="auth-explainer">链接会发送到你的邮箱。点击后会自动回到节奏并完成登录。</p>
      </form>
      <p class="auth-status" id="authStatus" role="status" aria-live="polite"></p>
      <footer>账号数据由 Supabase 加密传输，并按用户隔离。</footer>
    </div>
  </section>

  <div class="app-shell" id="workspace" data-mobile-view="tasks" hidden>
    <aside class="project-rail" aria-label="导航栏">
      <header class="brand-row">
        <div class="brand-mark" aria-hidden="true"><i data-lucide="calendar-check-2"></i></div>
        <div><strong>节奏</strong><span>先定下哪天，再放进时间</span></div>
      </header>

      <nav class="smart-nav" aria-label="智能列表">
        <button class="nav-item" data-filter="today"><i data-lucide="sun"></i><span>今天</span><b data-count="today">0</b></button>
        <button class="nav-item overdue-nav" data-filter="overdue"><i data-lucide="circle-alert"></i><span>已逾期</span><b data-count="overdue">0</b></button>
        <button class="nav-item active" data-filter="unplanned"><i data-lucide="list-todo"></i><span>待计划</span><b data-count="unplanned">0</b></button>
        <button class="nav-item" data-filter="links"><i data-lucide="bookmark"></i><span>阅读</span><b data-count="links">0</b></button>
        <button class="nav-item" data-filter="routines"><i data-lucide="repeat-2"></i><span>重复</span><b data-count="routines">0</b></button>
        <button class="nav-item" data-filter="completed"><i data-lucide="archive"></i><span>已完成</span><b data-count="completed">0</b></button>
      </nav>

      <section class="project-nav" aria-labelledby="projectsTitle">
        <div class="rail-section-title"><span id="projectsTitle">项目</span><button class="bare-icon" id="addProject" title="新建项目" aria-label="新建项目"><i data-lucide="plus"></i></button></div>
        <div id="projectList"></div>
      </section>

      <footer class="rail-footer">
        <div class="storage-status">
          <span class="status-dot"></span>
          <div><strong id="storageMode">本地离线</strong><span id="storageHint">数据仅保存在此设备</span></div>
        </div>
        <div class="account-status" id="accountStatus" hidden>
          <span id="accountEmail"></span>
          <button class="bare-icon" type="button" data-action="sign-out" title="退出登录" aria-label="退出登录"><i data-lucide="log-out"></i></button>
        </div>
      </footer>
    </aside>

    <section class="task-workbench" aria-label="任务面板">
      <header class="task-heading">
        <div><p class="eyebrow" id="filterEyebrow">待计划</p><h1 id="filterTitle">还没决定哪天，也不会被忘记</h1></div>
        <button class="icon-button task-sign-out" type="button" data-action="sign-out" title="退出登录" aria-label="退出登录" hidden><i data-lucide="log-out"></i></button>
      </header>

      <form class="quick-add" id="quickAdd">
        <div class="quick-main">
          <i data-lucide="circle-dashed" aria-hidden="true"></i>
          <input id="newTaskTitle" type="text" placeholder="输入任务，或直接粘贴一个链接…" autocomplete="off" required />
        </div>
        <div class="quick-options">
          <label><span>项目</span><select id="newTaskProject"><option value="">无项目</option></select></label>
          <label><span>预计</span><select id="newTaskDuration"><option value="15">15 分钟</option><option value="30">30 分钟</option><option value="45">45 分钟</option><option value="60" selected>1 小时</option><option value="90">1.5 小时</option><option value="120">2 小时</option></select></label>
          <label class="color-control"><span>颜色</span><button class="select-like" id="newTaskColor" type="button" aria-haspopup="listbox" aria-expanded="false"><i class="color-dot terracotta"></i><i data-lucide="chevron-down"></i></button><div class="color-popover" id="colorPopover" role="listbox" hidden></div></label>
          <button class="add-button" type="submit" title="添加任务"><i data-lucide="plus"></i><span>添加</span></button>
        </div>
      </form>

      <div class="task-scroll" id="taskGroups"></div>
    </section>

    <section class="calendar-workbench" aria-label="日程面板">
      <header class="calendar-toolbar">
        <div class="date-nav">
          <button class="icon-button" id="previous" title="上一个周期" aria-label="上一个周期"><i data-lucide="chevron-left"></i></button>
          <button class="today-button" id="calendarToday">今天</button>
          <button class="icon-button" id="next" title="下一个周期" aria-label="下一个周期"><i data-lucide="chevron-right"></i></button>
          <h2 id="periodTitle"></h2>
        </div>
        <div class="segmented" role="tablist" aria-label="日程视图">
          <button class="active" data-view="day" role="tab">日</button>
          <button data-view="week" role="tab">周</button>
          <button data-view="month" role="tab">月</button>
        </div>
      </header>
      <div class="calendar-stage" id="calendarStage"></div>
    </section>

    <nav class="mobile-nav" aria-label="移动端导航">
      <button class="active" data-mobile="tasks"><i data-lucide="circle-check-big"></i><span>任务</span></button>
      <button data-mobile="calendar"><i data-lucide="calendar-days"></i><span>日程</span></button>
      <button data-mobile="projects"><i data-lucide="folder-kanban"></i><span>项目</span></button>
    </nav>
  </div>

  <dialog id="taskDialog" class="task-dialog">
    <form id="editTaskForm" class="dialog-panel" method="dialog">
      <header><div><p class="eyebrow">任务详情</p><h2>编辑任务</h2></div><button class="icon-button" id="closeTaskDialog" type="button" aria-label="关闭"><i data-lucide="x"></i></button></header>
      <input id="editTaskId" type="hidden" />
      <input id="editSeriesId" type="hidden" />
      <div class="form-grid">
        <label class="wide"><span>任务名称</span><input id="editTitle" required /></label>
        <label><span>项目</span><select id="editProject"><option value="">无项目</option></select></label>
        <label><span>预计时间</span><select id="editDuration"><option value="15">15 分钟</option><option value="30">30 分钟</option><option value="45">45 分钟</option><option value="60">1 小时</option><option value="90">1.5 小时</option><option value="120">2 小时</option><option value="180">3 小时</option></select></label>
        <label class="wide"><span>计划日期</span><input id="editDate" type="date" /></label>
        <label class="check-row wide"><input id="editScheduled" type="checkbox" /><span>安排具体时间</span></label>
        <div class="schedule-fields wide" id="editScheduleFields">
          <label><span>开始时间</span><input id="editStart" type="time" step="900" /></label>
          <p>结束时间会根据预计时长自动计算</p>
        </div>
        <label class="wide"><span>链接</span><input id="editSourceUrl" type="url" placeholder="https://（可选）" /></label>
        <label class="check-row wide"><input id="editRepeating" type="checkbox" /><span>重复这项任务</span></label>
        <section class="recurrence-fields wide" id="recurrenceFields">
          <div class="recurrence-grid">
            <label><span>频率</span><select id="editRecurrence"><option value="daily">每天</option><option value="weekly">每周</option><option value="monthly">每月</option></select></label>
            <label><span>结束日期</span><input id="editRecurrenceEnd" type="date" /></label>
          </div>
          <fieldset class="weekday-picker" id="weekdayField">
            <legend>重复日期</legend>
            <div id="weekdayChoices"></div>
            <button class="weekday-preset" id="workdayPreset" type="button">工作日</button>
          </fieldset>
          <label class="check-row" id="applyFutureField"><input id="editApplyFuture" type="checkbox" /><span>将名称、项目、详情和预计时间用于后续任务</span></label>
        </section>
        <label class="wide"><span>详情</span><textarea id="editDetails" placeholder="背景、步骤、备注或你想留下的信息"></textarea></label>
      </div>
      <fieldset class="dialog-colors"><legend>颜色</legend><div id="editColors"></div></fieldset>
      <footer><div class="dialog-actions-left"><button class="danger-button" id="deleteTask" type="button"><i data-lucide="trash-2"></i>删除</button><button class="secondary-button" id="skipTask" type="button"><i data-lucide="skip-forward"></i>跳过本次</button></div><div><button class="secondary-button" id="cancelTaskDialog" type="button">取消</button><button class="primary-button" type="submit">保存更改</button></div></footer>
    </form>
  </dialog>

  <dialog id="projectDialog" class="project-dialog">
    <form id="projectForm" class="dialog-panel" method="dialog">
      <header><div><p class="eyebrow">项目</p><h2 id="projectDialogTitle">新建项目</h2></div><button class="icon-button" id="closeProjectDialog" type="button" aria-label="关闭"><i data-lucide="x"></i></button></header>
      <input id="projectId" type="hidden" />
      <label><span>项目名称</span><input id="projectName" placeholder="例如：毕业论文" required /></label>
      <fieldset class="dialog-colors"><legend>颜色</legend><div id="projectColors"></div></fieldset>
      <footer><button class="danger-button" id="deleteProject" type="button" hidden><i data-lucide="trash-2"></i>删除项目</button><div><button class="secondary-button" id="cancelProjectDialog" type="button">取消</button><button class="primary-button" id="projectSubmit" type="submit">创建项目</button></div></footer>
    </form>
  </dialog>

  <dialog id="deleteProjectDialog" class="confirm-dialog">
    <form class="dialog-panel confirm-panel" method="dialog">
      <header><div class="confirm-icon" aria-hidden="true"><i data-lucide="circle-alert"></i></div><div><p class="eyebrow">删除项目</p><h2 id="deleteProjectTitle">确认删除</h2></div></header>
      <p id="deleteProjectMessage"></p>
      <footer><span></span><div><button class="secondary-button" id="cancelDeleteProject" type="button">取消</button><button class="confirm-danger" id="confirmDeleteProject" type="button">删除项目</button></div></footer>
    </form>
  </dialog>

  <div class="toast" id="toast" role="status" aria-live="polite"></div>
`;

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];
const els = {
  authGate: $("#authGate"),
  authStatus: $("#authStatus"),
  shell: $("#workspace"),
  projectList: $("#projectList"),
  projectSelect: $("#newTaskProject"),
  editProject: $("#editProject"),
  title: $("#filterTitle"),
  eyebrow: $("#filterEyebrow"),
  taskGroups: $("#taskGroups"),
  stage: $("#calendarStage"),
  periodTitle: $("#periodTitle"),
  colorPopover: $("#colorPopover"),
  colorButton: $("#newTaskColor"),
  taskDialog: $("#taskDialog"),
  projectDialog: $("#projectDialog"),
  deleteProjectDialog: $("#deleteProjectDialog"),
  toast: $("#toast"),
};

initialize();

async function initialize() {
  bindStaticEvents();
  refreshIcons();

  if (!isSupabaseConfigured) {
    repository = createRepository(emptyWorkspace);
    state.workspace = await repository.loadWorkspace();
    showWorkspace();
    registerServiceWorker();
    return;
  }

  showAuthGate("正在恢复登录状态…", true);
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    showAuthGate(authErrorMessage(error));
  } else {
    await transitionToSession(data.session);
  }

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") return;
    queueMicrotask(() => transitionToSession(session));
  });
  registerServiceWorker();
}

function registerServiceWorker() {
  if ("serviceWorker" in navigator && import.meta.env.PROD) navigator.serviceWorker.register("/sw.js");
}

async function transitionToSession(session) {
  const revision = ++sessionRevision;
  if (!session?.user) {
    currentUser = null;
    repository = null;
    state.workspace = emptyWorkspace();
    showAuthGate();
    return;
  }

  if (currentUser?.id === session.user.id && repository?.mode === "supabase" && !els.shell.hidden) return;
  showAuthGate("正在同步你的日程…", true);
  const nextRepository = createRepository(emptyWorkspace, { client: supabase, userId: session.user.id });
  try {
    const workspace = await nextRepository.loadWorkspace();
    if (revision !== sessionRevision) return;
    repository = nextRepository;
    currentUser = session.user;
    state.workspace = workspace;
    showWorkspace();
  } catch (error) {
    console.error(error);
    if (revision !== sessionRevision) return;
    showAuthGate("账号已登录，但云端数据加载失败。请检查网络后重试登录。");
  }
}

function showWorkspace() {
  els.authGate.hidden = true;
  els.shell.hidden = false;
  renderAll();
  const cloudMode = repository?.mode === "supabase";
  $("#storageMode").textContent = cloudMode ? "Supabase 云同步" : "本地离线";
  $("#storageHint").textContent = cloudMode ? "已连接跨设备数据" : "数据仅保存在此设备";
  $("#accountStatus").hidden = !cloudMode;
  $$(".task-sign-out").forEach((button) => { button.hidden = !cloudMode; });
  if (cloudMode) {
    $("#accountEmail").textContent = currentUser.email || "已登录";
    $$("[data-action='sign-out']").forEach((button) => { button.title = `退出 ${currentUser.email || "当前账号"}`; });
  }
  refreshIcons();
}

function showAuthGate(message = "", busy = false) {
  els.shell.hidden = true;
  els.authGate.hidden = false;
  els.authGate.classList.toggle("restoring", busy);
  setAuthStatus(message, busy ? "neutral" : message ? "error" : "neutral");
  refreshIcons();
}

function selectAuthMethod(method) {
  state.authMethod = method;
  const passwordMode = method === "password";
  $("#passwordAuthForm").hidden = !passwordMode;
  $("#magicAuthForm").hidden = passwordMode;
  $$("[data-auth-method]").forEach((button) => {
    const active = button.dataset.authMethod === method;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  const source = passwordMode ? $("#magicEmail") : $("#passwordEmail");
  const target = passwordMode ? $("#passwordEmail") : $("#magicEmail");
  if (!target.value) target.value = source.value;
  setAuthStatus("");
  target.focus();
}

async function signInWithPassword(event) {
  event.preventDefault();
  const button = event.currentTarget.querySelector("[type='submit']");
  const email = $("#passwordEmail").value.trim();
  const password = $("#authPassword").value;
  await runAuthAction(button, "正在登录…", async () => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await transitionToSession(data.session);
  });
}

async function signUpWithPassword() {
  const form = $("#passwordAuthForm");
  if (!form.reportValidity()) return;
  const button = $("#signUpButton");
  const email = $("#passwordEmail").value.trim();
  const password = $("#authPassword").value;
  await runAuthAction(button, "正在创建…", async () => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: authRedirectUrl() },
    });
    if (error) throw error;
    if (data.session) await transitionToSession(data.session);
    else setAuthStatus("账号已创建。请打开确认邮件，完成验证后再登录。", "success");
  });
}

async function sendMagicLink(event) {
  event.preventDefault();
  const button = event.currentTarget.querySelector("[type='submit']");
  const email = $("#magicEmail").value.trim();
  await runAuthAction(button, "正在发送…", async () => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: authRedirectUrl() },
    });
    if (error) throw error;
    setAuthStatus("登录链接已发送。请在这台设备上打开邮件中的链接。", "success");
  });
}

async function runAuthAction(button, pendingLabel, action) {
  setAuthButtonsDisabled(true);
  setAuthButtonBusy(button, true, pendingLabel);
  setAuthStatus("");
  try {
    await action();
  } catch (error) {
    console.error(error);
    setAuthStatus(authErrorMessage(error), "error");
  } finally {
    setAuthButtonBusy(button, false);
    setAuthButtonsDisabled(false);
  }
}

function setAuthButtonBusy(button, busy, pendingLabel = "") {
  if (!button) return;
  const icon = button.querySelector("[data-lucide]");
  if (icon && busy) button.dataset.idleIcon = icon.dataset.lucide;
  if (icon) icon.dataset.lucide = busy ? "loader-circle" : button.dataset.idleIcon || icon.dataset.lucide;
  button.classList.toggle("busy", busy);
  const label = button.querySelector("span");
  if (label) label.textContent = busy ? pendingLabel : button.dataset.idleLabel;
  refreshIcons();
}

function setAuthButtonsDisabled(disabled) {
  $$(".auth-panel button").forEach((button) => { button.disabled = disabled; });
}

function setAuthStatus(message, tone = "neutral") {
  els.authStatus.textContent = message;
  els.authStatus.dataset.tone = tone;
}

function authErrorMessage(error) {
  const message = String(error?.message || error || "登录失败");
  if (/invalid login credentials/i.test(message)) return "Email 或密码不正确。";
  if (/email not confirmed/i.test(message)) return "请先打开确认邮件完成验证。";
  if (/user already registered/i.test(message)) return "这个 Email 已经注册，请直接登录。";
  if (/password should be at least/i.test(message)) return "密码至少需要 6 位。";
  if (/rate limit/i.test(message)) return "请求过于频繁，请稍后再试。";
  if (/failed to fetch|network/i.test(message)) return "无法连接云端，请检查网络后重试。";
  return message;
}

async function signOut() {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) {
    showToast(authErrorMessage(error));
    return;
  }
  await transitionToSession(null);
}

function bindStaticEvents() {
  $$("[data-auth-method]").forEach((button) => button.addEventListener("click", () => selectAuthMethod(button.dataset.authMethod)));
  $("#passwordAuthForm").addEventListener("submit", signInWithPassword);
  $("#signUpButton").addEventListener("click", signUpWithPassword);
  $("#magicAuthForm").addEventListener("submit", sendMagicLink);
  $$("[data-action='sign-out']").forEach((button) => button.addEventListener("click", signOut));
  $("#quickAdd").addEventListener("submit", addTask);
  els.colorButton.addEventListener("click", toggleColorPopover);
  document.addEventListener("click", (event) => {
    if (!event.target.closest(".color-control")) closeColorPopover();
  });
  $$(".nav-item").forEach((button) => button.addEventListener("click", () => setFilter(button.dataset.filter)));
  $("#addProject").addEventListener("click", () => openProjectDialog());
  $("#projectForm").addEventListener("submit", saveProject);
  $("#closeProjectDialog").addEventListener("click", () => els.projectDialog.close());
  $("#cancelProjectDialog").addEventListener("click", () => els.projectDialog.close());
  $("#deleteProject").addEventListener("click", requestProjectDelete);
  $("#cancelDeleteProject").addEventListener("click", () => els.deleteProjectDialog.close());
  $("#confirmDeleteProject").addEventListener("click", deleteProject);
  $("#editTaskForm").addEventListener("submit", saveEditedTask);
  $("#closeTaskDialog").addEventListener("click", () => els.taskDialog.close());
  $("#cancelTaskDialog").addEventListener("click", () => els.taskDialog.close());
  $("#deleteTask").addEventListener("click", deleteEditedTask);
  $("#skipTask").addEventListener("click", skipEditedTask);
  $("#editScheduled").addEventListener("change", syncPlanningFields);
  $("#editRepeating").addEventListener("change", syncRecurrenceFields);
  $("#editRecurrence").addEventListener("change", syncRecurrenceFields);
  $("#workdayPreset").addEventListener("click", () => { state.editWeekdays = [1, 2, 3, 4, 5]; renderWeekdayPicker(); });
  $("#calendarToday").addEventListener("click", () => { state.cursor = startOfDay(new Date()); renderCalendar(true); });
  $("#previous").addEventListener("click", () => shiftCursor(-1));
  $("#next").addEventListener("click", () => shiftCursor(1));
  $$("[data-view]").forEach((button) => button.addEventListener("click", () => setCalendarView(button.dataset.view)));
  $$("[data-mobile]").forEach((button) => button.addEventListener("click", () => setMobileView(button.dataset.mobile)));
  window.addEventListener("pointermove", resizeMove);
  window.addEventListener("pointerup", resizeEnd);
  window.addEventListener("focus", refreshDateContext);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshDateContext();
  });
  scheduleMidnightRefresh();
}

function renderAll() {
  renderProjects();
  renderTasks();
  renderCalendar(true);
  refreshIcons();
}

function renderProjects() {
  const activeProjects = state.workspace.projects.filter((project) => !project.isArchived).sort((a, b) => a.sortOrder - b.sortOrder);
  els.projectList.innerHTML = activeProjects.map((project) => {
    const count = activeTasks().filter((task) => task.projectId === project.id).length;
    return `<div class="project-row" data-project-id="${project.id}">
      <button class="project-item ${state.filter === `project:${project.id}` ? "active" : ""}" type="button"><i class="project-dot" style="--dot:${colorFor(project.color).solid}"></i><span>${escapeHtml(project.name)}</span><b>${count}</b></button>
      <button class="project-manage" type="button" aria-label="管理项目 ${escapeHtml(project.name)}" title="管理项目"><i data-lucide="more-horizontal"></i></button>
    </div>`;
  }).join("") || `<p class="rail-empty">还没有项目</p>`;
  $$(".project-row").forEach((row) => {
    row.querySelector(".project-item").addEventListener("click", () => setFilter(`project:${row.dataset.projectId}`));
    row.querySelector(".project-manage").addEventListener("click", () => openProjectDialog(row.dataset.projectId));
    bindProjectDropTarget(row);
  });
  const options = `<option value="">无项目</option>${activeProjects.map((project) => `<option value="${project.id}">${escapeHtml(project.name)}</option>`).join("")}`;
  els.projectSelect.innerHTML = options;
  els.editProject.innerHTML = options;
  if (state.filter.startsWith("project:")) els.projectSelect.value = state.filter.slice(8);
  updateCounts();
}

function updateCounts() {
  const active = activeTasks();
  const values = {
    today: active.filter((task) => taskDate(task) === state.todayKey).length,
    overdue: active.filter(isOverdue).length,
    unplanned: active.filter(isUnplanned).length,
    links: active.filter((task) => Boolean(task.sourceUrl)).length,
    routines: active.filter((task) => Boolean(task.recurrenceSeriesId)).length,
    completed: closedTasks().length,
  };
  Object.entries(values).forEach(([key, count]) => {
    const node = $(`[data-count="${key}"]`);
    if (node) node.textContent = count;
  });
}

function setFilter(filter) {
  state.filter = filter;
  $$(".nav-item").forEach((item) => item.classList.toggle("active", item.dataset.filter === filter));
  renderProjects();
  renderTasks();
  setMobileView("tasks");
}

function filteredTasks() {
  if (state.filter === "completed") return closedTasks();
  const active = activeTasks();
  if (state.filter === "unplanned") return active.filter(isUnplanned);
  if (state.filter === "overdue") return active.filter(isOverdue);
  if (state.filter === "links") return active.filter((task) => Boolean(task.sourceUrl));
  if (state.filter === "routines") return active.filter((task) => Boolean(task.recurrenceSeriesId));
  if (state.filter === "today") {
    return active.filter((task) => taskDate(task) === state.todayKey);
  }
  if (state.filter.startsWith("project:")) return active.filter((task) => task.projectId === state.filter.slice(8));
  return active;
}

function activeTasks() {
  return state.workspace.tasks.filter((task) => !task.completedAt && !task.skippedAt);
}

function closedTasks() {
  return state.workspace.tasks.filter((task) => task.completedAt || task.skippedAt);
}

function renderTasks() {
  const titles = filterCopy();
  els.eyebrow.textContent = titles.eyebrow;
  els.title.textContent = titles.title;
  const tasks = filteredTasks();
  const groups = taskGroupsFor(tasks);
  els.taskGroups.innerHTML = groups.map(taskGroupMarkup).join("");
  bindTaskCards();
  $$(".drop-zone").forEach(bindTaskDropZone);
  refreshIcons();
}

function taskGroupsFor(tasks) {
  if (state.filter === "completed") {
    return [{ key: "history", title: "完成记录", empty: "完成的任务会保存在这里", tasks: [...tasks].sort((a, b) => closedAt(b).localeCompare(closedAt(a))) }];
  }
  if (state.filter === "today") {
    return [
      { key: "dated", title: "待定时间", empty: "今天没有待定时间的任务", tasks: tasks.filter((task) => !activeEventForTask(task.id)) },
      { key: "scheduled", title: "已排时间", empty: "把上面的任务拖到日程中", tasks: tasks.filter((task) => activeEventForTask(task.id)).sort(compareTaskDate) },
    ];
  }
  if (state.filter === "overdue") {
    return [{ key: "overdue", title: "过期未完成", empty: "没有过期任务，节奏保持得很好", tasks: [...tasks].sort(compareTaskDate) }];
  }
  if (state.filter === "unplanned") {
    return [{ key: "unplanned", title: "待计划", empty: "所有任务都已经有了日期", tasks, drop: true }];
  }
  return [
    { key: "unplanned", title: "待计划", empty: "这里没有待计划任务", tasks: tasks.filter(isUnplanned), drop: true },
    { key: "planned", title: "已计划", empty: "这里还没有确定日期的任务", tasks: tasks.filter((task) => !isUnplanned(task)).sort(compareTaskDate) },
  ];
}

function taskGroupMarkup(group) {
  return `<section class="task-group ${group.drop ? "drop-zone" : ""}" ${group.drop ? 'data-drop="unplanned"' : ""}>
    <header><h2>${group.title}</h2><span>${group.tasks.length}</span></header>
    <div class="task-list">${group.tasks.length ? group.tasks.map(taskCard).join("") : emptyState(group.empty)}</div>
  </section>`;
}

function filterCopy() {
  if (state.filter === "today") return { eyebrow: "今天", title: "先看清今天，再决定具体时间" };
  if (state.filter === "overdue") return { eyebrow: "已逾期", title: "错过原计划，也可以重新安排" };
  if (state.filter === "links") return { eyebrow: "阅读", title: "值得留到有时间认真看的内容" };
  if (state.filter === "routines") return { eyebrow: "重复", title: "下一次会在完成或跳过后出现" };
  if (state.filter === "completed") return { eyebrow: "已完成", title: "做过的事，都有迹可循" };
  if (state.filter.startsWith("project:")) {
    const project = findProject(state.filter.slice(8));
    return { eyebrow: "项目", title: project?.name || "项目" };
  }
  return { eyebrow: "待计划", title: "还没决定哪天，也不会被忘记" };
}

function taskCard(task) {
  const project = findProject(task.projectId);
  const calendarEvent = activeEventForTask(task.id) || completedEventForTask(task.id);
  const closed = Boolean(task.completedAt || task.skippedAt);
  const overdue = isOverdue(task);
  const traits = [task.recurrenceSeriesId ? "重复" : null, task.sourceUrl ? "阅读" : null, project?.name].filter(Boolean);
  let timing = `${task.estimatedMinutes} 分钟`;
  if (calendarEvent) timing = `${formatShortDate(calendarEvent.startsAt)} · ${formatTimeRange(calendarEvent)}`;
  else if (task.plannedDate) timing = `${formatDateKey(task.plannedDate)} · 待定时间`;
  const completion = closed ? `${task.skippedAt ? "已跳过" : "完成于"} ${formatDateTime(closedAt(task))}` : null;
  const meta = [...traits, timing, completion].filter(Boolean).join(" · ");
  const overdueLabel = overdue ? `逾期 ${overdueDays(task)} 天` : "";
  return `<article class="task-card ${closed ? "completed" : ""} ${task.skippedAt ? "skipped" : ""} ${overdue ? "overdue" : ""}" draggable="${!closed}" data-task-id="${task.id}" style="${taskStyle(task)}" title="${escapeHtml(task.title)}">
    <button class="complete-task" type="button" title="${closed ? "恢复任务" : "完成任务"}" aria-label="${closed ? "恢复任务" : "完成任务"}"><i data-lucide="check"></i></button>
    <div class="task-card-copy"><div class="task-card-title">${task.sourceUrl ? `<i data-lucide="link-2"></i>` : ""}${task.recurrenceSeriesId ? `<i data-lucide="repeat-2"></i>` : ""}${escapeHtml(task.title)}</div><div class="task-card-meta"><span>${escapeHtml(meta)}</span>${overdue ? `<strong class="overdue-meta"><i data-lucide="circle-alert"></i>${overdueLabel}</strong>` : ""}</div></div>
    <button class="task-more" type="button" aria-label="编辑任务"><i data-lucide="more-horizontal"></i></button>
  </article>`;
}

function bindTaskCards() {
  $$(".task-card").forEach((card) => {
    const taskId = card.dataset.taskId;
    card.addEventListener("click", (event) => {
      if (!event.target.closest(".complete-task")) openTaskDialog(taskId);
    });
    card.querySelector(".complete-task").addEventListener("click", (event) => {
      event.stopPropagation();
      toggleTaskComplete(taskId);
    });
    if (card.draggable) {
      const calendarEvent = activeEventForTask(taskId);
      bindDraggable(card, { taskId, eventId: calendarEvent?.id || null });
    }
  });
}

function addTask(event) {
  event.preventDefault();
  const input = $("#newTaskTitle");
  const raw = input.value.trim();
  if (!raw) return;
  const parsed = parseSharedText(raw);
  const sourceUrl = parsed.sourceUrl;
  const contextualProject = state.filter.startsWith("project:") ? state.filter.slice(8) : null;
  const task = {
    id: crypto.randomUUID(),
    projectId: $("#newTaskProject").value || contextualProject,
    recurrenceSeriesId: null,
    generatedFromTaskId: null,
    title: parsed.title || raw,
    details: "",
    estimatedMinutes: Number($("#newTaskDuration").value),
    plannedDate: state.filter === "today" ? localDateKey(new Date()) : null,
    color: state.selectedColor,
    sourceUrl,
    sourceTitle: parsed.sourceTitle,
    sourceDomain: parsed.sourceDomain,
    completedAt: null,
    skippedAt: null,
    createdAt: new Date().toISOString(),
  };
  state.workspace.tasks.unshift(task);
  input.value = "";
  persistAndRender();
  showToast(sourceUrl ? "已加入阅读任务" : task.plannedDate ? "已加入今天" : "已加入待计划");
}

function toggleColorPopover(event) {
  event.stopPropagation();
  state.colorMenuOpen = !state.colorMenuOpen;
  els.colorPopover.hidden = !state.colorMenuOpen;
  els.colorButton.setAttribute("aria-expanded", String(state.colorMenuOpen));
  if (state.colorMenuOpen) {
    els.colorPopover.innerHTML = Object.entries(COLORS).map(([key, color]) => `<button type="button" role="option" aria-selected="${state.selectedColor === key}" data-color="${key}" title="${color.label}"><i class="color-dot ${key}"></i></button>`).join("");
    els.colorPopover.querySelectorAll("button").forEach((button) => button.addEventListener("click", () => selectQuickColor(button.dataset.color)));
  }
}

function selectQuickColor(color) {
  state.selectedColor = color;
  els.colorButton.querySelector(".color-dot").className = `color-dot ${color}`;
  closeColorPopover();
}

function closeColorPopover() {
  state.colorMenuOpen = false;
  els.colorPopover.hidden = true;
  els.colorButton.setAttribute("aria-expanded", "false");
}

function renderCalendar(shouldScroll = false) {
  if (state.view === "month") renderMonth();
  else renderTimeGrid();
  updatePeriodTitle();
  refreshIcons();
  if (shouldScroll && state.view !== "month") requestAnimationFrame(() => { els.stage.scrollTop = 8 * HOUR_HEIGHT - 24; });
}

function renderTimeGrid() {
  const days = state.view === "day" ? [startOfDay(state.cursor)] : weekDays(state.cursor);
  els.stage.innerHTML = `<div class="time-grid" style="--days:${days.length};--hour-height:${HOUR_HEIGHT}px;--quarter-height:${HOUR_HEIGHT / 4}px">
    <div class="calendar-corner"></div>
    ${days.map(dayHeader).join("")}
    <div class="plan-corner"><span>日期</span></div>
    ${days.map(dayPlanLane).join("")}
    <div class="time-rail">${Array.from({ length: 24 }, (_, hour) => `<div>${String(hour).padStart(2, "0")}:00</div>`).join("")}</div>
    ${days.map((day) => `<div class="day-column" data-date="${localDateKey(day)}"></div>`).join("")}
  </div>`;
  $$(".day-column").forEach(bindDayColumn);
  $$(".day-plan-lane").forEach(bindPlanLane);
  $$(".day-plan-chip").forEach(bindPlanChip);
  days.forEach((day) => renderEventsForDay(localDateKey(day)));
}

function dayHeader(day) {
  return `<div class="day-header ${isToday(day) ? "today" : ""}"><span>${DAY_NAMES[day.getDay()]}</span><b>${day.getDate()}</b></div>`;
}

function dayPlanLane(day) {
  const date = localDateKey(day);
  const tasks = dateOnlyTasksForDate(date);
  return `<div class="day-plan-lane" data-date="${date}">${tasks.length ? tasks.map((task) => {
    const overdue = isOverdue(task);
    return `<article class="day-plan-chip ${overdue ? "overdue" : ""}" draggable="true" data-task-id="${task.id}" style="${taskStyle(task)}" title="${escapeHtml(task.title)}"><i class="event-color"></i><span>${escapeHtml(task.title)}</span>${overdue ? `<i class="overdue-icon" data-lucide="circle-alert" aria-label="逾期 ${overdueDays(task)} 天"></i>` : ""}</article>`;
  }).join("") : `<span class="lane-empty">拖到这里，仅确定日期</span>`}</div>`;
}

function bindPlanChip(node) {
  node.addEventListener("click", () => openTaskDialog(node.dataset.taskId));
  bindDraggable(node, { taskId: node.dataset.taskId, eventId: null });
}

function renderEventsForDay(date) {
  const column = $(`.day-column[data-date="${date}"]`);
  if (!column) return;
  eventsForDate(date).forEach((calendarEvent) => {
    const task = findTask(calendarEvent.taskId);
    if (!task) return;
    const start = minutesOfDay(calendarEvent.startsAt);
    const duration = eventDuration(calendarEvent);
    const completed = Boolean(calendarEvent.completedAt || task.completedAt);
    const overdue = isOverdue(task);
    const article = document.createElement("article");
    article.className = `calendar-event ${completed ? "completed" : ""} ${overdue ? "overdue" : ""}`;
    article.dataset.eventId = calendarEvent.id;
    article.dataset.taskId = task.id;
    article.draggable = !completed;
    article.title = task.title;
    article.style.cssText = `top:${start / 60 * HOUR_HEIGHT}px;height:${Math.max(28, duration / 60 * HOUR_HEIGHT)}px;${taskStyle(task)}`;
    article.innerHTML = `<div class="resize-handle top" data-edge="top"></div><button class="event-check" type="button" aria-label="完成"><i data-lucide="check"></i></button><div class="event-copy"><strong><span>${escapeHtml(task.title)}</span>${overdue ? `<i class="overdue-icon" data-lucide="circle-alert" aria-label="逾期 ${overdueDays(task)} 天"></i>` : ""}</strong><span>${formatTimeRange(calendarEvent)}</span></div><div class="resize-handle bottom" data-edge="bottom"></div>`;
    article.addEventListener("click", (event) => {
      if (!event.target.closest(".event-check") && !event.target.closest(".resize-handle")) openTaskDialog(task.id);
    });
    article.querySelector(".event-check").addEventListener("click", (event) => { event.stopPropagation(); completeEvent(calendarEvent.id); });
    if (!completed) {
      bindDraggable(article, { eventId: calendarEvent.id, taskId: task.id });
      article.querySelectorAll(".resize-handle").forEach((handle) => handle.addEventListener("pointerdown", (event) => resizeStart(event, calendarEvent.id, handle.dataset.edge)));
    }
    column.appendChild(article);
  });
}

function renderMonth() {
  const first = new Date(state.cursor.getFullYear(), state.cursor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  els.stage.innerHTML = `<div class="month-grid">
    ${DAY_NAMES.map((name) => `<div class="month-weekday">${name}</div>`).join("")}
    ${days.map(monthCell).join("")}
  </div>`;
  $$(".month-cell").forEach(bindMonthCell);
  $$(".month-event").forEach((node) => {
    node.addEventListener("click", (event) => { event.stopPropagation(); openTaskDialog(node.dataset.taskId); });
    if (node.draggable) bindDraggable(node, { eventId: node.dataset.eventId || null, taskId: node.dataset.taskId });
  });
}

function monthCell(day) {
  const date = localDateKey(day);
  const items = calendarItemsForDate(date).slice(0, 4);
  const total = calendarItemsForDate(date).length;
  return `<div class="month-cell ${day.getMonth() !== state.cursor.getMonth() ? "outside" : ""} ${isToday(day) ? "today" : ""}" data-date="${date}"><b class="month-number">${day.getDate()}</b><div>${items.map(({ task, calendarEvent }) => {
    const completed = task.completedAt || calendarEvent?.completedAt;
    const overdue = isOverdue(task);
    const prefix = calendarEvent ? formatClock(minutesOfDay(calendarEvent.startsAt)) : "待定";
    return `<article class="month-event ${completed ? "completed" : ""} ${overdue ? "overdue" : ""}" draggable="${!completed}" ${calendarEvent ? `data-event-id="${calendarEvent.id}"` : ""} data-task-id="${task.id}" style="${taskStyle(task)}" title="${escapeHtml(task.title)}"><i class="event-color"></i><span>${prefix} ${escapeHtml(task.title)}</span>${overdue ? `<i class="overdue-icon" data-lucide="circle-alert" aria-label="逾期 ${overdueDays(task)} 天"></i>` : ""}</article>`;
  }).join("")}${total > 4 ? `<small>另有 ${total - 4} 项</small>` : ""}</div></div>`;
}

function calendarItemsForDate(date) {
  const eventItems = eventsForDate(date).map((calendarEvent) => ({ task: findTask(calendarEvent.taskId), calendarEvent })).filter((item) => item.task);
  const dateItems = dateOnlyTasksForDate(date).map((task) => ({ task, calendarEvent: null }));
  return [...eventItems, ...dateItems];
}

function bindDayColumn(column) {
  column.addEventListener("dragover", (event) => {
    if (!state.dragging) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    column.classList.add("drag-over");
    showDropPreview(column, dragPlacement(column, event.clientY));
  });
  column.addEventListener("dragleave", (event) => {
    if (event.relatedTarget && column.contains(event.relatedTarget)) return;
    column.classList.remove("drag-over");
    clearDropPreview(column);
  });
  column.addEventListener("drop", (event) => {
    event.preventDefault();
    const placement = state.dropPreview?.date === column.dataset.date
      ? state.dropPreview
      : dragPlacement(column, event.clientY);
    column.classList.remove("drag-over");
    clearDragFeedback();
    scheduleDrop(column.dataset.date, placement.startMinute);
  });
}

function bindPlanLane(lane) {
  lane.addEventListener("dragover", (event) => { event.preventDefault(); lane.classList.add("drag-over"); });
  lane.addEventListener("dragleave", () => lane.classList.remove("drag-over"));
  lane.addEventListener("drop", (event) => { event.preventDefault(); lane.classList.remove("drag-over"); clearDragFeedback(); planDrop(lane.dataset.date); });
}

function bindMonthCell(cell) {
  cell.addEventListener("dragover", (event) => { event.preventDefault(); cell.classList.add("drag-over"); });
  cell.addEventListener("dragleave", () => cell.classList.remove("drag-over"));
  cell.addEventListener("drop", (event) => {
    event.preventDefault();
    cell.classList.remove("drag-over");
    clearDragFeedback();
    if (state.dragging?.eventId) {
      const existing = findEvent(state.dragging.eventId);
      scheduleDrop(cell.dataset.date, existing ? minutesOfDay(existing.startsAt) : 9 * 60);
    } else {
      planDrop(cell.dataset.date);
    }
  });
  cell.addEventListener("dblclick", () => { state.cursor = dateKeyToDate(cell.dataset.date); setCalendarView("day"); });
}

function planDrop(date) {
  if (!state.dragging) return;
  const task = findTask(state.dragging.taskId);
  if (!task) return;
  const calendarEvent = state.dragging.eventId ? findEvent(state.dragging.eventId) : activeEventForTask(task.id);
  if (calendarEvent) state.workspace.events = state.workspace.events.filter((item) => item.id !== calendarEvent.id);
  task.plannedDate = date;
  clearDragFeedback();
  state.dragging = null;
  persistAndRender();
}

function dragPlacement(column, clientY) {
  const duration = dragDuration();
  const pointerMinute = (clientY - column.getBoundingClientRect().top) / HOUR_HEIGHT * 60;
  const grabOffset = state.dragging?.grabOffsetMinutes || 0;
  return { date: column.dataset.date, ...snappedTimePlacement(pointerMinute, duration, grabOffset, SNAP, DAY_MINUTES) };
}

function dragDuration() {
  if (!state.dragging) return 60;
  const existing = state.dragging.eventId ? findEvent(state.dragging.eventId) : activeEventForTask(state.dragging.taskId);
  const task = findTask(state.dragging.taskId);
  return clamp(existing ? eventDuration(existing) : task?.estimatedMinutes || 60, SNAP, DAY_MINUTES);
}

function showDropPreview(column, placement) {
  const previous = $(".day-column.drag-preview-active");
  if (previous && previous !== column) clearDropPreview(previous);
  let preview = column.querySelector(".drop-time-preview");
  if (!preview) {
    preview = document.createElement("div");
    preview.className = "drop-time-preview";
    preview.setAttribute("aria-hidden", "true");
    column.appendChild(preview);
  }
  const task = findTask(state.dragging?.taskId);
  const range = `${formatClock(placement.startMinute)} – ${formatClock(placement.startMinute + placement.duration)}`;
  preview.classList.toggle("compact", placement.duration <= 30);
  preview.style.cssText = `top:${placement.startMinute / 60 * HOUR_HEIGHT}px;height:${Math.max(HOUR_HEIGHT / 4, placement.duration / 60 * HOUR_HEIGHT)}px;${task ? taskStyle(task) : ""}`;
  preview.innerHTML = `<div class="drop-preview-copy">${placement.duration > 30 ? `<strong>${escapeHtml(task?.title || "任务")}</strong>` : ""}<span>${range}</span></div>`;
  column.classList.add("drag-preview-active");
  state.dropPreview = placement;
}

function clearDropPreview(column = null) {
  const columns = column ? [column] : $$(".day-column.drag-preview-active");
  columns.forEach((item) => {
    item.classList.remove("drag-preview-active", "drag-over");
    item.querySelector(".drop-time-preview")?.remove();
  });
  if (!column || state.dropPreview?.date === column.dataset.date) state.dropPreview = null;
}

function clearDragFeedback() {
  clearDropPreview();
  $$(".day-plan-lane.drag-over, .month-cell.drag-over, .task-group.drag-over, .project-row.drag-over").forEach((item) => item.classList.remove("drag-over"));
  state.dragGhost?.remove();
  state.dragGhost = null;
}

function scheduleDrop(date, startMinute) {
  if (!state.dragging) return;
  const task = findTask(state.dragging.taskId);
  if (!task) return;
  const existing = state.dragging.eventId ? findEvent(state.dragging.eventId) : activeEventForTask(task.id);
  const duration = existing ? eventDuration(existing) : task.estimatedMinutes || 60;
  const safeStart = clamp(startMinute, 0, DAY_MINUTES - duration);
  const startsAt = dateMinutesToIso(date, safeStart);
  const endsAt = dateMinutesToIso(date, safeStart + duration);
  if (existing) Object.assign(existing, { startsAt, endsAt });
  else state.workspace.events.push({ id: crypto.randomUUID(), taskId: task.id, startsAt, endsAt, completedAt: null });
  task.plannedDate = date;
  clearDragFeedback();
  state.dragging = null;
  persistAndRender();
}

function bindDraggable(node, payload) {
  node.addEventListener("dragstart", (event) => {
    clearDragFeedback();
    const existing = payload.eventId ? findEvent(payload.eventId) : null;
    const nodeRect = node.getBoundingClientRect();
    const grabOffsetMinutes = existing && node.classList.contains("calendar-event")
      ? clamp((event.clientY - nodeRect.top) / HOUR_HEIGHT * 60, 0, Math.max(0, eventDuration(existing) - SNAP))
      : 0;
    state.dragging = { ...payload, grabOffsetMinutes };
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", JSON.stringify(payload));
    const task = findTask(payload.taskId);
    const ghost = document.createElement("div");
    ghost.className = "calendar-drag-ghost";
    ghost.style.cssText = task ? taskStyle(task) : "";
    ghost.innerHTML = `<strong>${escapeHtml(task?.title || "任务")}</strong>${existing ? `<span>${formatTimeRange(existing)}</span>` : ""}`;
    document.body.appendChild(ghost);
    state.dragGhost = ghost;
    event.dataTransfer.setDragImage(ghost, 18, 18);
    node.classList.add("dragging");
  });
  node.addEventListener("dragend", () => { clearDragFeedback(); state.dragging = null; node.classList.remove("dragging"); });
}

function bindTaskDropZone(zone) {
  zone.addEventListener("dragover", (event) => { event.preventDefault(); zone.classList.add("drag-over"); });
  zone.addEventListener("dragleave", () => zone.classList.remove("drag-over"));
  zone.addEventListener("drop", (event) => {
    event.preventDefault();
    zone.classList.remove("drag-over");
    if (!state.dragging) return;
    const task = findTask(state.dragging.taskId);
    if (!task) return;
    state.workspace.events = state.workspace.events.filter((item) => item.taskId !== task.id || item.completedAt);
    task.plannedDate = null;
    clearDragFeedback();
    state.dragging = null;
    persistAndRender();
  });
}

function bindProjectDropTarget(row) {
  row.addEventListener("dragover", (event) => {
    if (!state.dragging) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    row.classList.add("drag-over");
  });
  row.addEventListener("dragleave", (event) => {
    if (event.relatedTarget && row.contains(event.relatedTarget)) return;
    row.classList.remove("drag-over");
  });
  row.addEventListener("drop", (event) => {
    event.preventDefault();
    row.classList.remove("drag-over");
    if (!state.dragging) return;
    const task = findTask(state.dragging.taskId);
    const project = findProject(row.dataset.projectId);
    if (!task || !project) return;
    if (task.projectId === project.id) {
      clearDragFeedback();
      state.dragging = null;
      showToast(`任务已在“${project.name}”中`);
      return;
    }
    task.projectId = project.id;
    clearDragFeedback();
    state.dragging = null;
    persistAndRender();
    showToast(`已移入“${project.name}”`);
  });
}

function resizeStart(event, eventId, edge) {
  event.preventDefault();
  event.stopPropagation();
  const calendarEvent = findEvent(eventId);
  state.resizing = { eventId, edge, startY: event.clientY, originalStart: new Date(calendarEvent.startsAt), originalEnd: new Date(calendarEvent.endsAt) };
  event.currentTarget.setPointerCapture(event.pointerId);
}

function resizeMove(event) {
  if (!state.resizing) return;
  const calendarEvent = findEvent(state.resizing.eventId);
  if (!calendarEvent) return;
  const delta = snap((event.clientY - state.resizing.startY) / HOUR_HEIGHT * 60);
  const dayStart = startOfDay(state.resizing.originalStart).getTime();
  const dayEnd = dayStart + DAY_MINUTES * 60000;
  if (state.resizing.edge === "bottom") {
    const minimum = new Date(calendarEvent.startsAt).getTime() + SNAP * 60000;
    calendarEvent.endsAt = new Date(clamp(state.resizing.originalEnd.getTime() + delta * 60000, minimum, dayEnd)).toISOString();
  } else {
    const maximum = new Date(calendarEvent.endsAt).getTime() - SNAP * 60000;
    calendarEvent.startsAt = new Date(clamp(state.resizing.originalStart.getTime() + delta * 60000, dayStart, maximum)).toISOString();
  }
  renderCalendar(false);
}

function resizeEnd() {
  if (!state.resizing) return;
  const calendarEvent = findEvent(state.resizing.eventId);
  const task = calendarEvent && findTask(calendarEvent.taskId);
  if (calendarEvent && task) {
    task.estimatedMinutes = eventDuration(calendarEvent);
    task.plannedDate = localDateKey(calendarEvent.startsAt);
  }
  state.resizing = null;
  persist();
  renderTasks();
}

function toggleTaskComplete(taskId) {
  const task = findTask(taskId);
  if (!task) return;
  if (task.completedAt || task.skippedAt) {
    task.completedAt = null;
    task.skippedAt = null;
    state.workspace.events.filter((event) => event.taskId === taskId).forEach((event) => { event.completedAt = null; });
    showToast("任务已恢复");
  } else {
    completeTask(task);
  }
  persistAndRender();
}

function completeEvent(eventId) {
  const calendarEvent = findEvent(eventId);
  const task = calendarEvent && findTask(calendarEvent.taskId);
  if (!calendarEvent || !task) return;
  completeTask(task);
  persistAndRender();
}

function completeTask(task) {
  const now = new Date().toISOString();
  task.completedAt = now;
  task.skippedAt = null;
  state.workspace.events.filter((event) => event.taskId === task.id).forEach((event) => { event.completedAt = now; });
  const generated = ensureNextOccurrence(task);
  showToast(generated ? "已完成，下一次任务已生成" : "任务已完成");
}

function ensureNextOccurrence(task) {
  const series = findSeries(task.recurrenceSeriesId);
  if (!series || !series.isActive) return null;
  const currentDate = taskDate(task) || series.startDate;
  const nextDate = nextOccurrenceDate(currentDate, series.rule, series.endDate);
  if (!nextDate) {
    series.isActive = false;
    return null;
  }
  const existing = state.workspace.tasks.find((item) => item.recurrenceSeriesId === series.id && item.plannedDate === nextDate && !item.completedAt && !item.skippedAt);
  if (existing) return existing;
  const nextTask = {
    id: crypto.randomUUID(),
    projectId: series.projectId,
    recurrenceSeriesId: series.id,
    generatedFromTaskId: task.id,
    title: series.title,
    details: series.details,
    estimatedMinutes: series.estimatedMinutes,
    plannedDate: nextDate,
    color: series.color,
    sourceUrl: series.sourceUrl,
    sourceTitle: series.sourceTitle,
    sourceDomain: series.sourceDomain,
    completedAt: null,
    skippedAt: null,
    createdAt: new Date().toISOString(),
  };
  state.workspace.tasks.unshift(nextTask);
  return nextTask;
}

function openTaskDialog(taskId) {
  const task = findTask(taskId);
  if (!task) return;
  const series = findSeries(task.recurrenceSeriesId);
  const scheduledEvent = activeEventForTask(task.id) || completedEventForTask(task.id);
  $("#editTaskId").value = task.id;
  $("#editSeriesId").value = series?.id || "";
  $("#editTitle").value = task.title;
  $("#editProject").value = task.projectId || "";
  $("#editDuration").value = String(task.estimatedMinutes);
  $("#editDate").value = taskDate(task) || "";
  $("#editScheduled").checked = Boolean(scheduledEvent && !scheduledEvent.completedAt);
  $("#editStart").value = formatClock(scheduledEvent ? minutesOfDay(scheduledEvent.startsAt) : 9 * 60);
  $("#editSourceUrl").value = task.sourceUrl || "";
  $("#editRepeating").checked = Boolean(series && series.isActive);
  $("#editRecurrence").value = series?.rule?.frequency || "weekly";
  $("#editRecurrenceEnd").value = series?.endDate || "";
  $("#editApplyFuture").checked = false;
  $("#editDetails").value = task.details || "";
  state.editWeekdays = [...(series?.rule?.weekdays || [dateKeyToDate(task.plannedDate || localDateKey(new Date())).getDay()])];
  renderEditColorPicker(task, task.color);
  renderWeekdayPicker();
  syncPlanningFields();
  syncRecurrenceFields();
  $("#skipTask").hidden = !series || Boolean(task.completedAt || task.skippedAt);
  els.taskDialog.showModal();
  $("#editTitle").focus();
  refreshIcons();
}

function syncPlanningFields() {
  if ($("#editScheduled").checked && !$("#editDate").value) $("#editDate").value = localDateKey(state.cursor);
  $("#editScheduleFields").hidden = !$("#editScheduled").checked;
}

function syncRecurrenceFields() {
  if ($("#editRepeating").checked && !$("#editDate").value) $("#editDate").value = localDateKey(state.cursor);
  $("#recurrenceFields").hidden = !$("#editRepeating").checked;
  $("#weekdayField").hidden = $("#editRecurrence").value !== "weekly";
  $("#applyFutureField").hidden = !$("#editSeriesId").value;
}

function renderWeekdayPicker() {
  $("#weekdayChoices").innerHTML = WEEKDAY_SHORT.map((label, day) => `<button type="button" class="weekday-choice ${state.editWeekdays.includes(day) ? "active" : ""}" data-weekday="${day}" aria-pressed="${state.editWeekdays.includes(day)}">${label}</button>`).join("");
  $$(".weekday-choice").forEach((button) => button.addEventListener("click", () => {
    const day = Number(button.dataset.weekday);
    state.editWeekdays = state.editWeekdays.includes(day) ? state.editWeekdays.filter((item) => item !== day) : [...state.editWeekdays, day].sort((a, b) => a - b);
    if (!state.editWeekdays.length) state.editWeekdays = [day];
    renderWeekdayPicker();
  }));
}

function renderEditColorPicker(task, color) {
  task._draftColor = color;
  renderColorChoices("#editColors", color, (nextColor) => renderEditColorPicker(task, nextColor));
}

function saveEditedTask(event) {
  event.preventDefault();
  const task = findTask($("#editTaskId").value);
  if (!task) return;
  task.title = $("#editTitle").value.trim();
  task.projectId = $("#editProject").value || null;
  task.estimatedMinutes = Number($("#editDuration").value);
  task.plannedDate = $("#editDate").value || null;
  task.details = $("#editDetails").value.trim();
  task.color = task._draftColor || task.color;
  task.sourceUrl = $("#editSourceUrl").value.trim() ? normalizeUrl($("#editSourceUrl").value.trim()) : null;
  task.sourceDomain = task.sourceUrl ? sourceDomain(task.sourceUrl) : null;

  const scheduledEvent = activeEventForTask(task.id);
  if ($("#editScheduled").checked && task.plannedDate) {
    const startMinute = timeInputToMinutes($("#editStart").value || "09:00");
    const startsAt = dateMinutesToIso(task.plannedDate, startMinute);
    const endsAt = dateMinutesToIso(task.plannedDate, startMinute + task.estimatedMinutes);
    if (scheduledEvent) Object.assign(scheduledEvent, { startsAt, endsAt });
    else state.workspace.events.push({ id: crypto.randomUUID(), taskId: task.id, startsAt, endsAt, completedAt: null });
  } else if (scheduledEvent) {
    state.workspace.events = state.workspace.events.filter((calendarEvent) => calendarEvent.id !== scheduledEvent.id);
  }

  updateTaskRecurrence(task);
  delete task._draftColor;
  els.taskDialog.close();
  persistAndRender();
}

function updateTaskRecurrence(task) {
  const existing = findSeries($("#editSeriesId").value);
  if (!$("#editRepeating").checked) {
    if (existing) existing.isActive = false;
    task.recurrenceSeriesId = null;
    return;
  }
  if (!task.plannedDate) return;
  const rule = normalizeRecurrenceRule({
    frequency: $("#editRecurrence").value,
    weekdays: state.editWeekdays,
    monthDay: dateKeyToDate(task.plannedDate).getDate(),
  }, task.plannedDate);
  const series = existing || {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  };
  series.rule = rule;
  series.startDate ||= task.plannedDate;
  series.endDate = $("#editRecurrenceEnd").value || null;
  series.isActive = true;
  if (!existing || $("#editApplyFuture").checked) updateSeriesDefaults(series, task);
  if (!existing) state.workspace.recurrences.push(series);
  task.recurrenceSeriesId = series.id;
}

function updateSeriesDefaults(series, task) {
  Object.assign(series, {
    projectId: task.projectId,
    title: task.title,
    details: task.details,
    estimatedMinutes: task.estimatedMinutes,
    color: task.color,
    sourceUrl: task.sourceUrl,
    sourceTitle: task.sourceTitle,
    sourceDomain: task.sourceDomain,
  });
}

function skipEditedTask() {
  const task = findTask($("#editTaskId").value);
  if (!task || !task.recurrenceSeriesId || task.completedAt || task.skippedAt) return;
  const now = new Date().toISOString();
  task.skippedAt = now;
  state.workspace.events.filter((event) => event.taskId === task.id).forEach((event) => { event.completedAt = now; });
  const generated = ensureNextOccurrence(task);
  els.taskDialog.close();
  persistAndRender();
  showToast(generated ? "已跳过，下一次任务已生成" : "已跳过，本系列已经结束");
}

function deleteEditedTask() {
  const id = $("#editTaskId").value;
  const task = findTask(id);
  state.workspace.tasks = state.workspace.tasks.filter((item) => item.id !== id);
  state.workspace.events = state.workspace.events.filter((event) => event.taskId !== id);
  const series = task && findSeries(task.recurrenceSeriesId);
  if (series && !activeTasks().some((item) => item.recurrenceSeriesId === series.id)) series.isActive = false;
  els.taskDialog.close();
  persistAndRender();
}

function openProjectDialog(projectId = null) {
  const project = projectId ? findProject(projectId) : null;
  $("#projectId").value = project?.id || "";
  $("#projectDialogTitle").textContent = project ? "编辑项目" : "新建项目";
  $("#projectName").value = project?.name || "";
  $("#projectSubmit").textContent = project ? "保存更改" : "创建项目";
  $("#deleteProject").hidden = !project;
  renderProjectColorPicker(project?.color || DEFAULT_COLOR);
  els.projectDialog.showModal();
  $("#projectName").focus();
}

function saveProject(event) {
  event.preventDefault();
  const name = $("#projectName").value.trim();
  if (!name) return;
  const color = $("#projectColors").dataset.color || DEFAULT_COLOR;
  const project = findProject($("#projectId").value);
  if (project) Object.assign(project, { name, color });
  else state.workspace.projects.push({ id: crypto.randomUUID(), name, color, sortOrder: state.workspace.projects.length, isArchived: false });
  els.projectDialog.close();
  persistAndRender();
  showToast(project ? "项目已更新" : "项目已创建");
}

function requestProjectDelete() {
  const project = findProject($("#projectId").value);
  if (!project) return;
  const taskCount = state.workspace.tasks.filter((task) => task.projectId === project.id).length;
  const recurrenceCount = state.workspace.recurrences.filter((series) => series.projectId === project.id).length;
  els.deleteProjectDialog.dataset.projectId = project.id;
  $("#deleteProjectTitle").textContent = `删除“${project.name}”？`;
  $("#deleteProjectMessage").textContent = `项目将被删除，${taskCount} 个任务会保留并移至“无项目”${recurrenceCount ? `，${recurrenceCount} 条重复规则也会解除项目归属` : ""}。`;
  els.projectDialog.close();
  els.deleteProjectDialog.showModal();
}

function deleteProject() {
  const projectId = els.deleteProjectDialog.dataset.projectId;
  const project = findProject(projectId);
  if (!project) return;
  const { taskCount } = deleteProjectFromWorkspace(state.workspace, projectId);
  if (state.filter === `project:${projectId}`) {
    state.filter = "unplanned";
    $$(".nav-item").forEach((item) => item.classList.toggle("active", item.dataset.filter === state.filter));
  }
  els.deleteProjectDialog.close();
  delete els.deleteProjectDialog.dataset.projectId;
  persistAndRender();
  showToast(`项目已删除，${taskCount} 个任务已移至无项目`);
}

function renderProjectColorPicker(color) {
  $("#projectColors").dataset.color = color;
  renderColorChoices("#projectColors", color, renderProjectColorPicker);
}

function renderColorChoices(selector, selected, onSelect) {
  const root = $(selector);
  root.innerHTML = Object.entries(COLORS).map(([key, color]) => `<button class="color-choice ${selected === key ? "active" : ""}" type="button" data-color="${key}" title="${color.label}" aria-label="${color.label}"><i class="color-dot ${key}"></i></button>`).join("");
  root.querySelectorAll("button").forEach((button) => button.addEventListener("click", () => onSelect(button.dataset.color)));
}

function setCalendarView(view) {
  state.view = view;
  $$("[data-view]").forEach((button) => button.classList.toggle("active", button.dataset.view === view));
  renderCalendar(true);
}

function shiftCursor(direction) {
  if (state.view === "day") state.cursor = addDays(state.cursor, direction);
  else if (state.view === "week") state.cursor = addDays(state.cursor, direction * 7);
  else state.cursor = new Date(state.cursor.getFullYear(), state.cursor.getMonth() + direction, 1);
  renderCalendar(true);
}

function updatePeriodTitle() {
  if (state.view === "day") els.periodTitle.textContent = `${state.cursor.getFullYear()} 年 ${state.cursor.getMonth() + 1} 月 ${state.cursor.getDate()} 日 · ${DAY_NAMES[state.cursor.getDay()]}`;
  else if (state.view === "week") {
    const days = weekDays(state.cursor);
    els.periodTitle.textContent = `${formatShortDate(days[0])} – ${formatShortDate(days[6])}`;
  } else els.periodTitle.textContent = `${state.cursor.getFullYear()} 年 ${state.cursor.getMonth() + 1} 月`;
}

function setMobileView(view) {
  state.mobileView = view;
  els.shell.dataset.mobileView = view;
  $$("[data-mobile]").forEach((button) => button.classList.toggle("active", button.dataset.mobile === view));
  if (view === "calendar" && state.view !== "month") requestAnimationFrame(() => { els.stage.scrollTop = 8 * HOUR_HEIGHT - 24; });
}

function persistAndRender() {
  persist();
  renderAll();
}

function persist() {
  if (!repository) return;
  const targetRepository = repository;
  const snapshot = structuredClone(state.workspace);
  saveQueue = saveQueue
    .catch(() => undefined)
    .then(() => targetRepository.saveWorkspace(snapshot))
    .catch((error) => {
      console.error(error);
      showToast("保存失败，请检查连接");
    });
}

function findProject(id) { return state.workspace.projects.find((project) => project.id === id); }
function findTask(id) { return state.workspace.tasks.find((task) => task.id === id); }
function findEvent(id) { return state.workspace.events.find((event) => event.id === id); }
function findSeries(id) { return state.workspace.recurrences.find((series) => series.id === id); }
function activeEventForTask(id) { return state.workspace.events.find((event) => event.taskId === id && !event.completedAt); }
function completedEventForTask(id) { return state.workspace.events.find((event) => event.taskId === id && event.completedAt); }
function eventsForDate(date) { return state.workspace.events.filter((event) => localDateKey(event.startsAt) === date).sort((a, b) => a.startsAt.localeCompare(b.startsAt)); }
function dateOnlyTasksForDate(date) { return activeTasks().filter((task) => task.plannedDate === date && !activeEventForTask(task.id)); }
function taskDate(task) { return taskEffectiveDate(task, state.workspace.events); }
function isOverdue(task) { return isTaskOverdue(task, state.workspace.events, state.todayKey); }
function overdueDays(task) { return taskOverdueDays(task, state.workspace.events, state.todayKey); }
function isUnplanned(task) { return !task.plannedDate && !activeEventForTask(task.id); }
function closedAt(task) { return task.completedAt || task.skippedAt || ""; }
function compareTaskDate(a, b) { return String(taskDate(a) || "9999").localeCompare(String(taskDate(b) || "9999")); }
function colorFor(name) { return COLORS[name] || COLORS[DEFAULT_COLOR]; }
function taskStyle(task) { const color = colorFor(task.color); return `--task-solid:${color.solid};--task-soft:${color.soft};--task-ink:${color.ink};`; }
function emptyState(text) { return `<div class="empty-state"><i data-lucide="circle-dashed"></i><span>${text}</span></div>`; }
function refreshIcons() { createIcons({ icons: ICONS, attrs: { "stroke-width": 1.8 } }); }
function showToast(message) { els.toast.textContent = message; els.toast.classList.add("visible"); clearTimeout(showToast.timer); showToast.timer = setTimeout(() => els.toast.classList.remove("visible"), 2200); }
function normalizeUrl(value) { return /^https?:\/\//i.test(value) ? value : `https://${value}`; }
function sourceDomain(value) { try { return new URL(value).hostname.replace(/^www\./, ""); } catch { return null; } }
function escapeHtml(value) { return String(value ?? "").replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]); }
function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }
function snap(value) { return Math.round(value / SNAP) * SNAP; }
function startOfDay(date) { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
function addDays(date, count) { const next = new Date(date); next.setDate(next.getDate() + count); return next; }
function weekDays(date) { const start = addDays(startOfDay(date), -date.getDay() + 1); return Array.from({ length: 7 }, (_, index) => addDays(start, index)); }
function isToday(date) { return localDateKey(date) === state.todayKey; }
function dateMinutesToIso(date, minutes) { const target = dateKeyToDate(date); target.setHours(0, minutes, 0, 0); return target.toISOString(); }
function minutesOfDay(value) { const date = new Date(value); return date.getHours() * 60 + date.getMinutes(); }
function timeInputToMinutes(value) { const [hours, minutes] = value.split(":").map(Number); return hours * 60 + minutes; }
function formatClock(minutes) { return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`; }
function formatTimeRange(event) { return `${formatClock(minutesOfDay(event.startsAt))} – ${formatClock(minutesOfDay(event.endsAt))}`; }
function formatShortDate(value) { const date = value instanceof Date ? value : new Date(value); return `${date.getMonth() + 1}/${date.getDate()}`; }
function formatDateKey(value) { const date = dateKeyToDate(value); return `${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}
function formatDateTime(value) { const date = new Date(value); return `${date.getMonth() + 1}/${date.getDate()} ${formatClock(date.getHours() * 60 + date.getMinutes())}`; }

function refreshDateContext() {
  const todayKey = localDateKey(new Date());
  scheduleMidnightRefresh();
  if (todayKey === state.todayKey) return;
  state.todayKey = todayKey;
  if (!els.shell.hidden) renderAll();
}

function scheduleMidnightRefresh() {
  clearTimeout(midnightRefreshTimer);
  const now = new Date();
  const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
  midnightRefreshTimer = setTimeout(refreshDateContext, nextMidnight - now);
}
